import { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import NotificationIcon from "../../../../assets/icons/NotificationIcon";
import { SocketStore, useApiHook } from "@bjsdistribution/tms-component-library";
import { Skeleton, Spin } from "antd";
import { useNavigate } from "react-router-dom";

type Notification = {
  id: number;
  client_id: number;
  organization_id: number | null;
  user_uuid: string;
  message: string;
  note_type: string;
  notification_type: string;
  is_active: boolean;
  is_deleted: boolean;
  snooze: string | null;
  socket_url: string | null;
  url: string | null;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
};

export function timeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  const intervals: { [key: string]: number } = {
    year: 31536000,
    month: 2592000,
    week: 604800,
    day: 86400,
    hour: 3600,
    minute: 60,
    second: 1,
  };
  for (const [unit, value] of Object.entries(intervals)) {
    const amount = Math.floor(seconds / value);
    if (amount >= 1) {
      return amount === 1 ? `${amount} ${unit} ago` : `${amount} ${unit}s ago`;
    }
  }
  return "just now";
}

export default function NotificationPopup({ open, setOpen, isSupplier }: { open: boolean; setOpen: any; isSupplier?: boolean }) {
  let socketInst = SocketStore.get();
  const navigate = useNavigate();
  const popupRef = useRef<HTMLDivElement>(null);
  const listInnerRef = useRef<HTMLDivElement>(null);

  const [showUnread, setShowUnread] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [page, setPage] = useState(1);

  const { fetchData: markAsRead, loading: markLoading } = useApiHook<any>({
    endpoint: "notifications/api/mark_read_notifications/",
    method: "POST",
  });

  const {
    data: fetchedData,
    fetchData: fetchNotificationData,
    loading,
  } = useApiHook<any>({
    endpoint: `notifications/get_user_notification/?user_type=${isSupplier ? "supplier" : "client"}`,
    method: "GET",
  });

  const loadNotifications = (pageNumber = 1) => {
    fetchNotificationData({
      endPointQuery: `${showUnread ? "&filter_by_status=unread" : ""}&page=${pageNumber}`,
    });
  };

  useEffect(() => {
    setPage(1);
    loadNotifications(1);
  }, [showUnread, open]);

  useEffect(() => {
    if (fetchedData?.data) {
      setUnreadCount(fetchedData?.unread_count);
      if (page === 1) setNotifications(fetchedData.data);
      else setNotifications((prev) => [...prev, ...fetchedData.data]);
    }
  }, [fetchedData?.data, page]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const onScroll = () => {
    if (listInnerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = listInnerRef.current;
      if (scrollTop + clientHeight === scrollHeight && !loading) {
        if (notifications.length <= fetchedData?.total_count) {
          const nextPage = page + 1;
          setPage(nextPage);
          loadNotifications(nextPage);
        }
      }
    }
  };

  const handleMarkAsRead = async (id: any, isActive: boolean, url: string) => {
    try {
      if (!isActive || markLoading) {
        url && navigate(url);
        setOpen(false);

        return;
      }
      await markAsRead({
        endPointQuery: `?notification_id=${id}`,
      });
      setNotifications((prev) => prev.map((p) => (p.id === id ? { ...p, is_active: false } : p)));
      setUnreadCount(unreadCount - 1);

      url && navigate(url);
      setOpen(false);
    } catch (error) {
      console.log(error, "<====err");
    }
  };

  useEffect(() => {
    if (!socketInst) return;

    const handleNotificationListen = (res: any) => {
      console.log(res, "<===res");
      setUnreadCount((pre) => Number(pre) + 1);
      if (open) {
        setPage(1);
        loadNotifications(1);
      }
    };

    socketInst.on("notification", handleNotificationListen);

    return () => {
      socketInst.off("notification", handleNotificationListen);
    };
  }, [socketInst]);

  return (
    <Wrapper ref={popupRef}>
      <NotificationWrapper onClick={() => setOpen(!open)}>
        <NotificationIcon />
        <span>{unreadCount}</span>
      </NotificationWrapper>

      {open && (
        <Popup>
          <Header>
            <Title>Notifications</Title>
            <ToggleWrapper>
              <span>Only show unread</span>
              <ToggleInput type="checkbox" checked={showUnread} onChange={() => setShowUnread((prev) => !prev)} />
            </ToggleWrapper>
          </Header>

          <NotificationList ref={listInnerRef} onScroll={onScroll}>
            {loading && page === 1 ? (
              <>
                <Skeleton active paragraph={{ rows: 2 }} />
                <Skeleton active paragraph={{ rows: 2 }} />
              </>
            ) : notifications.length > 0 ? (
              notifications.map((n) => (
                <NotificationItem key={n.id} unread={n.is_active} onClick={() => handleMarkAsRead(n.id, n.is_active, n.url)}>
                  <strong>{n.note_type}</strong>
                  <span>{n.message}</span>
                  <small>{n.created_at ? timeAgo(n.created_at) : "--"}</small>
                </NotificationItem>
              ))
            ) : (
              <EmptyState>No notifications</EmptyState>
            )}
            {(loading || markLoading) && page > 1 && (
              <LoadMoreWrapper>
                <Spin size="small" />
              </LoadMoreWrapper>
            )}
          </NotificationList>
        </Popup>
      )}
    </Wrapper>
  );
}

// Styled components (existing)
const Wrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const Popup = styled.div`
  position: absolute;
  top: 40px;
  right: 0;
  width: 420px;
  max-height: 520px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  overflow-y: auto;
  z-index: 1000;
  display: flex;
  flex-direction: column;
`;

/* Header */
const Header = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h4`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
`;

const ToggleWrapper = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: #555;
  cursor: pointer;
`;

const ToggleInput = styled.input`
  accent-color: #007bff;
`;

const NotificationList = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const NotificationItem = styled.div<{ unread?: boolean }>`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid #eee;
  padding: 10px 14px;
  transition: background 0.2s ease;
  cursor: pointer;
  background: ${({ unread }) => (unread ? "#f8fbff" : "white")};

  &:hover {
    background: #f2f6fa;
  }

  strong {
    color: #333;
  }

  span {
    color: #666;
    font-size: 0.85rem;
  }

  small {
    color: #999;
    font-size: 0.75rem;
  }
`;

const EmptyState = styled.div`
  padding: 20px;
  text-align: center;
  color: #888;
`;

const NotificationWrapper = styled.div`
  position: relative;
  cursor: pointer;

  span {
    display: block;
    position: absolute;
    min-width: 20px;
    height: 20px;
    background: ${({ theme }: any) => theme.primaryRed};
    border-radius: 100px;
    color: ${({ theme }: any) => theme.primaryWhite};
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
    top: -12px;
    right: -14px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 4px;
    cursor: pointer;
  }
`;

const LoadMoreWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 10px 0;
`;
